/* eslint-disable max-lines-per-function */
/**
 * Salesforce Live Agent Adapter
 * @returns {void}
 */
(function() {
  'use strict';
  window.salesforceLiveAgentAdapter = function(environment, customConfig) {
    return function(chatbot) {

      let isChattingWithAgent = false;
      let isRequestingEvents = false;
      let isUserTyping = false;

      const timers = {
        requestEventsTimer: 0,
        waitForAgentTimer: 0
      };

      // Default Salesforce chat configuration
      const defaultSalesforceConf = {
        debug: environment === 'development',
        endpoint: 'https://<salesforce-connector-api-endpoint>',
        sendUserTypingStatus: false, // sends more requests, use only if necessary
        agentName: 'Agent',
        agentWaitTimeout: 1200, // seconds
        getMessagesTimeout: 30, // seconds
        requestEventsDelay: 3, // seconds
        inputId: '#inbenta-bot-input',
        labels: {
          waitingForFile: 'Agent is requesting you a file (click on link icon)',
          uploadingFile: 'Uploading file, please wait',
          fileUploaded: 'File uploaded correctly',
          fileUploadError: 'Error on file upload',
          caseCreated: 'A new case was created',
          caseNumber: 'case number',
          caseError: 'Error on create a case, try again later'
        }
      };

      const soundNotificationsConf = {
        active: true,
        source: 'https://freesound.org/data/previews/171/171671_2437358-lq.mp3'
      };

      const config = extend(defaultSalesforceConf, customConfig);
      config.endpoint = (environment === 'development') ? config.endpoints.staging : config.endpoints.production;

      // Clean domain path
      const domain = config.endpoint.replace(/\/$/, '');

      // Full API paths
      const path = {
        availability: domain + '/availability',
        init: domain + '/init',
        getMessages: domain + '/message/receive',
        sendMessage: domain + '/message/send',
        getFileForm: domain + '/file/getForm',
        uploadFileIframe: domain + '/uploadFileIframe.html',
        processFile: domain + '/file/process',
        createCase: domain + '/createCase'
      };

      // File transfer variables
      let fileTransfer = {
        filePicked: false,
        serverFileName: '',
        originalFileName: '',
        mimeType: '',
        extension: '',
        fileToken: '',
        uploadServletUrl: ''
      };

      // Define Chatbot messages to be used
      const chatbotMessage = {
        chatClosed: function() {
          chatbot.actions.displaySystemMessage({
            message: 'chat-closed',
            translate: true
          });
        },
        waitForAgent: function() {
          chatbot.actions.displaySystemMessage({
            message: 'wait-for-agent',
            translate: true
          });
        },
        agentLeft: function() {
          chatbot.actions.displaySystemMessage({
            message: 'agent-left',
            replacements: { agentName: AgentSession.get('sfla_agentName') || config.agentName },
            translate: true
          });
        },
        agentJoined: function() {
          chatbot.actions.displaySystemMessage({
            message: 'agent-joined',
            replacements: { agentName: AgentSession.get('sfla_agentName') || config.agentName },
            translate: true
          });
        },
        noAgents: function() {
          chatbot.actions.sendMessage({
            directCall: 'escalationNoAgentsAvailable'
          });
        },
        enterQuestion: function() {
          chatbot.actions.displayChatbotMessage({
            type: 'answer',
            message: 'enter-question',
            translate: true
          });
        },
        waitingForFile: function() {
          chatbot.actions.displaySystemMessage({
            message: config.labels.waitingForFile
          });
        },
        uploadingFile: function() {
          chatbot.actions.displaySystemMessage({
            message: config.labels.uploadingFile
          });
        },
        fileUploaded: function() {
          chatbot.actions.displaySystemMessage({
            message: config.labels.fileUploaded
          });
        },
        fileUploadError: function() {
          chatbot.actions.displaySystemMessage({
            message: config.labels.fileUploadError
          });
        }
      };

      // Define agent class
      const AgentSession = {
        errorCounter: 0,
        get: function(key) {
          return localStorage.getItem(key);
        },
        set: function(key, value) {
          localStorage.setItem(key, value);
        },
        clear: function() {
          localStorage.removeItem('sfla_isActive');
          localStorage.removeItem('sfla_id');
          localStorage.removeItem('sfla_key');
          localStorage.removeItem('sfla_affinityToken');
        }
      };

      chatbot.subscriptions.onResetSession(function(next) {
        isChattingWithAgent = false;
        AgentSession.clear();
        return next();
      });

      /**
       * Simple is object check.
       * @param {any} item -
       * @returns {boolean} -
       */
      function isObject(item) {
        return (item && typeof item === 'object' && !Array.isArray(item));
      }

      /**
       * Deep merge two objects.
       * @param {any} target -
       * @param {any} source -
       * @returns {object} target
       */
      function extend(target, source) {
        if (isObject(target) && isObject(source)) {
          for (const key in source) {
            if (isObject(source[key])) {
              if (!target[key]) Object.assign(target, { [key]: {} });
              extend(target[key], source[key]);
            } else {
              Object.assign(target, { [key]: source[key] });
            }
          }
        }
        return target;
      }

      const requestCall = async function(requestOptions, callback = null) {
        const options = extend({
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            'instance': config.instance
          },
          mode: 'cors'
        }, requestOptions);

        if (options.headers['Content-Type'] === 'application/json') {
          options.data = JSON.stringify(options.data);
        }

        const response = await fetch(options.url, {
          method: options.method,
          headers: options.headers,
          body: options.data,
          mode: options.mode
        });

        const data = await response.json();

        dd('Request Response', response.status, data);

        if (callback && typeof callback === 'function') await callback(data);
      };

      const checkAgentsAvailability = async function(callback) {
        const options = {
          url: path.availability
        };

        await requestCall(options, callback);
      };

      // Inicialize Salesforce connection
      const initSalesforceConnection = function(params) {
        chatbotMessage.waitForAgent();
        chatbot.actions.disableInput();

        const options = {
          url: path.init,
          data: params
        };

        const callback = function(response) {
          if (response.status === 'success') {
            AgentSession.set('sfla_isActive', 'true');
            AgentSession.set('sfla_id', response.sfla_id);
            AgentSession.set('sfla_key', response.sfla_key);
            AgentSession.set('sfla_affinityToken', response.sfla_affinityToken);
            timers.requestEventsTimer = setInterval(retrieveSalesforceEvents, config.requestEventsDelay * 1000);
          } else {
            endChatSession(false, 'Reason: Adapter received failure response (Create Chat)');
            chatbotMessage.noAgents();
          }
        };

        requestCall(options, callback);
      };

      // Get Salesforce events
      const retrieveSalesforceEvents = function() {
        if (!isChattingWithAgent || isRequestingEvents) {
          return;
        }

        dd('Retrieving SF Events...');

        const options = {
          url: path.getMessages,
          data: {
            SFLA_KEY: AgentSession.get('sfla_key'),
            SFLA_AFFINITY_TOKEN: AgentSession.get('sfla_affinityToken'),
            SFLA_TIMEOUT: config.getMessagesTimeout
          }
        };

        const callback = function(response) {
          isRequestingEvents = false;
          if (response.status === 'error') {
            AgentSession.errorCounter++;
          } else if (response.messages instanceof Object) {
            response.messages.forEach(function(message) {
              switch (message.type) {
                case 'AgentNotTyping':
                  chatbot.actions.hideChatbotActivity();
                  break;
                case 'AgentTyping':
                  chatbot.actions.displayChatbotActivity();
                  break;
                case 'ChatEstablished':
                  isChattingWithAgent = true;
                  AgentSession.set('sfla_agentName', message.agentName);
                  chatbotMessage.agentJoined();
                  clearTimeout(timers.waitForAgentTimer);
                  chatbot.api.track('CHAT_ATTENDED', { 'sflaId': AgentSession.get('sfla_id') });
                  sendChatbotHistory();
                  chatbot.actions.enableInput();
                  break;
                case 'ChatMessage':
                  dd('ChatMessage', message.text);
                  chatbot.actions.hideChatbotActivity();
                  chatbot.actions.displayChatbotMessage({
                    type: 'answer',
                    message: message.text
                  });
                  break;
                case 'ChatRequestFail':
                  chatbot.api.track('CHAT_UNATTENDED');
                  clearTimeout(timers.waitForAgentTimer);
                  endChatSession(true, 'Reason: Adapter received "ChatRequestFail" response from Salesforce');
                  chatbotMessage.noAgents();
                  break;
                case 'AgentDisconnect':
                case 'ChatEnded':
                  endChatSession(true, 'Reason: Adapter received "ChatEnded" response from Salesforce');
                  hideUploadMediaButton();
                  chatbotMessage.agentLeft();
                  chatbotMessage.chatClosed();
                  chatbotMessage.enterQuestion();
                  break;
                case 'ChatTransferred':
                  chatbotMessage.agentLeft();
                  AgentSession.set('sfla_agentName', message.agentName);
                  chatbotMessage.agentJoined();
                  break;
                case 'FileTransfer':
                  if (message.transferType === 'Requested') {
                    setFileTransferOptions(message.fileToken, message.uploadServletUrl);
                    showUploadMediaButton();
                    chatbotMessage.waitingForFile();
                  } else { // File transfer canceled or completed
                    hideUploadMediaButton();
                  }
                  break;
                case 'SessionExpired':
                  endChatSession(false, 'Reason: Session Expired');
                  chatbotMessage.chatClosed();
                  chatbotMessage.enterQuestion();
                  return;
                default:
                  dd('missingEvent - ' + message.type);
              }
            });
          }

          if (AgentSession.errorCounter >= 3) {
            dd('Error counter: ' + AgentSession.errorCounter);
            endChatSession(true, 'Reason: Adapter received to many errors. Error counter limit');
            chatbotMessage.chatClosed();
            chatbotMessage.enterQuestion();
          }
        };

        isRequestingEvents = true;
        requestCall(options, callback);
      };

      const showUploadMediaButton = function() {
        const iframe = document.createElement('iframe');
        iframe.src = path.uploadFileIframe;
        iframe.id = 'file-iframe';

        const footer = document.querySelector('.footer__form');
        footer.prepend(iframe);
      };

      const hideUploadMediaButton = function() {
        const iframe = document.getElementById('file-iframe');
        if (iframe) iframe.remove();
        setFileTransferOptions('', '');
      };

      // Send message to Salesforce agent
      const sendMessageToAgent = function(message, callback) {
        dd('sendMessageToAgent', message);

        const options = {
          url: path.sendMessage,
          data: {
            message,
            SFLA_KEY: AgentSession.get('sfla_key'),
            SFLA_AFFINITY_TOKEN: AgentSession.get('sfla_affinityToken')
          }
        };

        requestCall(options, callback);
      };

      // End chat session
      const endChatSession = function(sendNotice, reason) {
        reason = reason !== undefined ? reason : '';
        dd('endChatSession', reason);

        clearInterval(timers.requestEventsTimer);

        isChattingWithAgent = false;
        AgentSession.set('sfla_isActive', 'false');

        const callback = function() {
          chatbot.actions.hideChatbotActivity();
          chatbot.actions.enableInput();
        };

        if (sendNotice) {
          sendMessageToAgent({
            type: 'ChatEnd',
            text: null
          }, callback);
        }
      };

      // Send user is typing event to Salesforce
      // eslint-disable-next-line no-unused-vars
      const sendUserIsTyping = function(listenerID, targetElement, chatbotInstance) {
        if (!isChattingWithAgent) return;

        let message;
        const inputLength = targetElement[0].value.length;

        if (inputLength > 0 && !isUserTyping) {
          isUserTyping = true;
          message = { type: 'ChasitorTyping', text: null };
        } else if (inputLength <= 0 && isUserTyping && event.which !== 13) {
          isUserTyping = false;
          message = { type: 'ChasitorNotTyping', text: null };
        }

        if (!message) return;

        sendMessageToAgent(message);
      };

      // Get Chatbot transcript
      const sendChatbotHistory = function() {
        sendMessageToAgent({
          type: 'ChatMessage',
          text: getTranscript()
        });
      };

      // Define file transfer options
      const setFileTransferOptions = function(fileToken, uploadServletUrl) {
        fileTransfer = {
          filePicked: false,
          serverFileName: '',
          originalFileName: '',
          mimeType: '',
          extension: '',
          fileToken,
          uploadServletUrl
        };
      };

      const createCase = function(formData) {
        const options = {
          url: path.createCase,
          data: formData
        };

        const callback = function(response) {
          let messageResponse = config.labels.caseError;
          if (response.status === 'success') {
            messageResponse = config.labels.caseCreated;
            if (response.case !== undefined || response.case !== '') {
              messageResponse += ' (' + config.labels.caseNumber + ': ' + response.case + ')';
            }
          }
          chatbot.actions.hideChatbotActivity();
          chatbot.actions.enableInput();
          chatbot.actions.displaySystemMessage({
            message: messageResponse
          });
        };

        requestCall(options, callback);
      };

      const prepareMessage = (message) => {
        return message
          .replace(/<\/?[^>]+>/gi, '') // Strip HTML
          .replace(/(r\n|\r|\n){1,}/g, '\n') // Replace multiple linebreaks with just one
          .replace(/\t/g, '') // Remove tabs
          .trimStart('\n') // Remove leading breaklines
          .trimEnd('\n'); // Remove trailing breaklines
      };

      const getTranscript = function() {
        const conversation = chatbot.actions.getConversationTranscript();

        let fullConversation = '';
        for (const message of conversation) {
          if (message.message !== '') {
            fullConversation += message.user === 'assistant' ? '🤖 ChatBot' : '👤 User';
            fullConversation += ': ' + prepareMessage(message.message) + '\n';
          }
        }

        if (fullConversation !== '') {
          fullConversation = 'CONVERSATION HISTORY\n\n' + fullConversation;
        }

        return fullConversation;
      };

      // Detect escalationOffer content
      chatbot.subscriptions.onDisplayChatbotMessage(async function(messageData, next) {
        if ('attributes' in messageData && messageData.attributes !== null && 'DIRECT_CALL' in messageData.attributes && messageData.attributes.DIRECT_CALL === 'escalationOffer') {
          await checkAgentsAvailability(async function(response) {
            await chatbot.api.addVariable('agents_available', response.isAvailable.toString());
          });
        } else if ('actions' in messageData && messageData.actions.length > 0) {
          for (let i = 0; i < messageData.actions.length; i++) {
            if (!('parameters' in messageData.actions[i])) continue;
            if (!('callback' in messageData.actions[i].parameters)) continue;

            if (messageData.actions[i].parameters.callback === 'createTicket') {
              chatbot.actions.disableInput();
              chatbot.actions.displayChatbotActivity();
              const formData = messageData.actions[i].parameters.data;
              formData.TRANSCRIPT = getTranscript();
              formData.LAST_NAME = formData.LAST_NAME ?? 'Inbenta';
              createCase(formData);
            }
          }
        }
        return next(messageData);
      });

      // Start live chat connection
      chatbot.subscriptions.onEscalateToAgent(async function(escalationData, next) {
        const checkAgentsCallback = function(response) {
          if (response.status === 'success' && response.isAvailable) {
            isChattingWithAgent = true;
            timers.waitForAgentTimer = setTimeout(function() {
              if (!isChattingWithAgent) return;
              endChatSession(true, 'Reason: Timeout. No agent answer');
              chatbotMessage.noAgents();
            }, config.agentWaitTimeout * 1000);
            initSalesforceConnection(escalationData);
          } else {
            chatbotMessage.noAgents();
          }
        };

        await checkAgentsAvailability(checkAgentsCallback);

        return next(escalationData);
      });

      chatbot.subscriptions.onStartConversation(async(next) => {
        await checkAgentsAvailability(async function(response) {
          if (response.status === 'success') {
            await chatbot.api.addVariable('agents_available', response.isAvailable.toString());
          }
        });
        return next();
      });

      // Trigger escalation start
      chatbot.subscriptions.onEscalationStart(async function(escalationData, next) {
        await checkAgentsAvailability(async function(response) {
          if (response.status === 'success') {
            await chatbot.api.addVariable('agents_available', response.isAvailable.toString());
            await chatbot.actions.sendMessage({ directCall: 'escalationStart' });
          }
        });
        return next(escalationData);
      });

      // Decide whether the message has to be sent to Chatbot or Live Agent
      chatbot.subscriptions.onSendMessage(function(messageData, next) {
        if (isChattingWithAgent) {
          if (messageData.message !== '') {
            sendMessageToAgent({
              type: 'ChatMessage',
              text: messageData.message
            });
          }
          return null;
        } else {
          return next(messageData);
        }
      });

      // On Chatbot ready check if there was a conversation with an agent
      chatbot.subscriptions.onDomReady(function(next) {
        handleFileListener();
        if (config.sendUserTypingStatus) {
          chatbot.helpers.setListener(config.inputId, 'keyup', sendUserIsTyping, chatbot);
        }
        if (AgentSession.get('sfla_isActive') === 'true') {
          isChattingWithAgent = true;
          timers.requestEventsTimer = setInterval(retrieveSalesforceEvents, config.requestEventsDelay * 1000);
        }
        return next();
      });

      // End conversation if user closes Chatbot window
      chatbot.subscriptions.onSelectSystemMessageOption(function(optionData, next) {
        const isCloseButton = optionData.id === 'exitConversation';
        const isYesValue = optionData.option && optionData.option.value === 'yes';
        const isChatActive = AgentSession.get('sfla_isActive');
        if (isCloseButton && isYesValue && isChatActive === 'true') {
          clearTimeout(timers.waitForAgentTimer);
          endChatSession(true, 'Reason: Customer exited conversation');
          chatbotMessage.chatClosed();
          chatbotMessage.enterQuestion();
        }
        return next(optionData);
      });

      // play a notification sound on incoming message from an agent
      chatbot.subscriptions.onDisplayChatbotMessage(async(messageData, next) => {
        await next(messageData);
        if (isChattingWithAgent) {
          playSound();
        }
      });

      // notification sound trigger
      let cacheSound;
      const playSound = () => {
        const focused = document.hasFocus();

        if (!focused) {
          if (soundNotificationsConf.active) {
            if (!cacheSound) {
              cacheSound = new Audio(soundNotificationsConf.source);
            }
            cacheSound.play();
          }
        }
      };

      const onUploadMedia = function() {
        chatbotMessage.uploadingFile();

        const options = {
          url: path.getFileForm
        };

        const callback = async function(response) {
          if (response.status === 'success') {
            fileTransfer.serverFileName = response.serverFileName;
            document.getElementById('file-iframe').contentWindow.postMessage(JSON.parse(JSON.stringify({
              fileForm: response
            })), path.uploadFileIframe);
          } else {
            chatbotMessage.fileUploadError();
            hideUploadMediaButton();
          }
        };

        requestCall(options, callback);
      };

      const processFile = function() {
        const options = {
          url: path.processFile,
          data: {
            originalFileName: fileTransfer.originalFileName,
            serverFileName: fileTransfer.serverFileName,
            fileMimeType: fileTransfer.mimeType,
            fileExtension: fileTransfer.extension,
            fileToken: fileTransfer.fileToken,
            uploadServletUrl: fileTransfer.uploadServletUrl,
            SFLA_KEY: AgentSession.get('sfla_key'),
            SFLA_AFFINITY_TOKEN: AgentSession.get('sfla_affinityToken')
          }
        };

        const callback = function(response) {
          if (response.status === 'success') {
            chatbotMessage.fileUploaded();
          } else {
            chatbotMessage.fileUploadError();
            hideUploadMediaButton();
          }
        };

        requestCall(options, callback);
      };

      const handleFileListener = function() {
        const callback = (event) => {
          const data = event?.data;
          if (data === 'fileUploaded') processFile();
          else if (data instanceof Object && 'filePicked' in data) {
            fileTransfer.filePicked = data.filePicked;
            fileTransfer.originalFileName = data.originalFileName;
            fileTransfer.extension = data.originalFileName.split('.').pop();
            fileTransfer.mimeType = data.mimeType;

            if (fileTransfer.filePicked) {
              onUploadMedia();
            }
          }
        };
        window.addEventListener('message', callback);
      };

      // Debug function
      const dd = function() {
        if (config.debug) {
          const date = new Date(new Date().toUTCString()).toISOString();
          const dateColor = 'background: #222; color: #bada55';
          const title = (arguments[0]) ? arguments[0] : '';
          const titleColor = 'background: #222; color: white';
          const args = Array.prototype.slice.call(arguments);
          args.shift();
          args.unshift('%c' + date + '%c ' + title, dateColor, titleColor);
          console.log.apply(console, args);
        }
      };
    };
  };
})();

